<script>
  import { fly } from 'svelte/transition';
  import { onMount, onDestroy } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { fetchDeliveries } from '../../controllers/deliveryController'; 
  import { fetchTransporte } from '../../controllers/transporteController';
  import { fetchVehiculos } from '../../controllers/vehiculoController';
  import { fetchEscoltas } from '../../controllers/escoltaController';
  import { fetchUsers } from '../../controllers/userController';
  import { fetchCorredores } from '../../controllers/corredorController';
  import DeliveryDetails from './DeliveryDetails.svelte';
  import DeliveryMap from './DeliveryMap.svelte';
  import DestinationMap from './DestinationMap.svelte';
  import DefineDelivery from './DefineDelivery.svelte';
  import Gallery from './Gallery.svelte';
  import pb from '../../lib/pocketbase';
  import { getEgresoDets } from '../../models/egresoDetModel';

  const dispatch = createEventDispatcher();

  export let selectedItem;
  export let closePane;
  export let status;

  let activeTab = 'details'; // Default tab
  let deliveries = []; // Fetched deliveries
  let transportes = []; // Fetched transportes
  let vehiculos = []; // Fetched vehiculos
  let escoltas = []; // Fetched escoltas
  let usuarios = []; // Fetched users
  let corredores = []; // Feteched corredores
  let refreshInterval;

  // Function to refresh egresoDet data
  async function refreshEgresoDet() {
    if (selectedItem) {
      const egresoDets = await getEgresoDets(selectedItem.id, 1, 100);
      if (egresoDets) {
        selectedItem.egresoDet = egresoDets.items;
      }
    }
  }

  // Fetch deliveries and transportes when the component mounts
  onMount(async () => {
    const deliveriesResult = await fetchDeliveries(1, 100); // Fetch first page with 10 items per page
    if (deliveriesResult) {
      deliveries = deliveriesResult.delivery; // Store fetched deliveries
    }

    const transporteResult = await fetchTransporte(1, 100); // Fetch first page with 10 items per page
    if (transporteResult) {
      transportes = transporteResult.transporte; // Store fetched transportes
    }

    const vehiculoResult = await fetchVehiculos(1, 100); // Fetch first page with 10 items per page
    if (vehiculoResult) {
      vehiculos = vehiculoResult.vehiculo; // Store fetched vehiculos
    }

    const escoltaResult = await fetchEscoltas(1, 100); // Fetch first page with 10 items per page
    if (escoltaResult) {
      escoltas = escoltaResult.escolta; // Store fetched escoltas
    }

    const usuarioResult = await fetchUsers(1,100);
    if (usuarioResult){
      usuarios = usuarioResult.users; // Store fetched users
    }

    const corredorResult = await fetchCorredores(1,100);
    if (corredorResult){
      corredores = corredorResult.corredor; // Store fetched corredores
    }

    // Initial fetch of egresoDet
    await refreshEgresoDet();

    // Set up periodic refresh
    refreshInterval = setInterval(refreshEgresoDet, 2000); // Refresh every 2 seconds
  });

  onDestroy(() => {
    // Clear the refresh interval
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
  });

  // Status colors for styling
  const statusColors = {
    NOT_READY: 'bg-red-200 border-red-300 text-red-700',
    PACKING: 'bg-yellow-500 border-yellow-600 text-yellow-900',
    PARTIALLY_READY: 'bg-orange-200 border-orange-300 text-orange-700',
    READY: 'bg-teal-200 border-teal-300 text-teal-700',
    ON_ROUTE: 'bg-lightBlue-200 border-lightBlue-300 text-lightBlue-700',
    DELIVERED: 'bg-emerald-200 border-emerald-300 text-emerald-700',
    RETURNED: 'bg-red-200 border-red-300 text-red-700',
    DEFAULT: 'bg-gray-100 border-gray-300 text-gray-700',
  };

  // Get the appropriate status color
  function getStatusColor() {
    return statusColors[status] || statusColors.DEFAULT;
  }
</script>

{#if selectedItem}
  <div
    transition:fly={{ x: 300, duration: 400 }}
    class={`fixed top-0 right-0 w-1/2 h-full shadow-lg border-l border-blueGray-200 z-50 flex flex-col ${getStatusColor()}`}
  >
    <!-- Header with Close Button - Fixed Position -->
    <div class="p-4 border-b border-blueGray-200">
      <button
        class="absolute top-4 right-4 text-blueGray-500 hover:text-red-500"
        on:click={closePane}
      >
        ✕
      </button>

      <!-- Tab Navigation - Fixed at top -->
      <div class="mt-4">
        <nav class="flex overflow-x-auto pb-2" aria-label="Tabs">
          <button
            class={`py-2 px-4 border-b-2 font-medium text-sm flex-shrink-0 mx-2 ${
              activeTab === 'details'
                ? 'border-blueGray-500 text-blueGray-600'
                : 'border-transparent text-blueGray-500 hover:text-blueGray-700 hover:border-blueGray-300'
            }`}
            on:click={() => activeTab = 'details'}
          >
            Detalles del Pedido
          </button>
          
          {#if selectedItem.sta === 'NOT_READY' || selectedItem.sta === 'READY' || selectedItem.sta === 'PARTIALLY_READY' || selectedItem.sta === 'ON_ROUTE'}
            <button
              class={`py-2 px-4 border-b-2 font-medium text-sm flex-shrink-0 mx-2 ${
                activeTab === 'destination-map'
                  ? 'border-blueGray-500 text-blueGray-600'
                  : 'border-transparent text-blueGray-500 hover:text-blueGray-700 hover:border-blueGray-300'
              }`}
              on:click={() => activeTab = 'destination-map'}
            >
              Ubicación Destino
            </button>
          {/if}
          
          {#if selectedItem.sta === 'DELIVERED'}
            <button
              class={`py-2 px-4 border-b-2 font-medium text-sm flex-shrink-0 mx-2 ${
                activeTab === 'delivery-map'
                  ? 'border-blueGray-500 text-blueGray-600'
                  : 'border-transparent text-blueGray-500 hover:text-blueGray-700 hover:border-blueGray-300'
              }`}
              on:click={() => activeTab = 'delivery-map'}
            >
              Ubicación de Entrega
            </button>
          {/if}
          
          {#if selectedItem.sta === 'PARTIALLY_READY' || selectedItem.sta === 'READY' || selectedItem.sta === 'PACKING'}
            <button
              class={`py-2 px-4 border-b-2 font-medium text-sm flex-shrink-0 mx-2 ${
                activeTab === 'define-delivery'
                  ? 'border-blueGray-500 text-blueGray-600'
                  : 'border-transparent text-blueGray-500 hover:text-blueGray-700 hover:border-blueGray-300'
              }`}
              on:click={() => activeTab = 'define-delivery'}
            >
              Método de Entrega
            </button>
          {/if}
          
          {#if selectedItem.sta === 'DELIVERED'}
            <button
              class={`py-2 px-4 border-b-2 font-medium text-sm flex-shrink-0 mx-2 ${
                activeTab === 'gallery'
                  ? 'border-blueGray-500 text-blueGray-600'
                  : 'border-transparent text-blueGray-500 hover:text-blueGray-700 hover:border-blueGray-300'
              }`}
              on:click={() => activeTab = 'gallery'}
            >
              Galería
            </button>
          {/if}
        </nav>
      </div>
    </div>

    <!-- Scrollable Content Area -->
    <div class="flex-1 overflow-y-auto p-4">
      <!-- Tab Content -->
      {#if activeTab === 'details'}
        <DeliveryDetails {selectedItem} />
      {:else if activeTab === 'delivery-map'}
        <DeliveryMap {selectedItem}  {deliveries} {transportes} {vehiculos} {escoltas} />
      {:else if activeTab === 'destination-map'}
        <DestinationMap {selectedItem} {dispatch} {closePane} />
      {:else if activeTab === 'define-delivery'}
        <DefineDelivery {selectedItem} {deliveries} {transportes} {vehiculos} {escoltas} {dispatch} {closePane} />
      {:else if activeTab === 'gallery'}
        <Gallery {selectedItem} />
      {/if}
    </div>
  </div>
{/if}

<style>
  .gallery-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px;
  }

  .thumbnail {
    width: 100px;
    text-align: center;
  }

  .thumbnail-image {
    width: 100%;
    height: auto;
    border-radius: 5px;
  }

  .thumbnail-caption {
    margin-top: 5px;
    font-size: 0.8em;
    color: #666;
  }
</style>